import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Camera, Trash2, LogOut } from 'lucide-react';
import { AvatarCropDialog } from './AvatarCropDialog';

const accountFormSchema = z.object({
  display_name: z.string().min(1, 'Display name is required').max(50, 'Display name must be less than 50 characters'),
  email: z.string().email('Invalid email address'),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  location: z.string().max(100, 'Location must be less than 100 characters').optional(),
});

export const AccountSettings = () => {
  const { currentUser, accountType, refreshUserData, logout } = useUser();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(currentUser?.avatar_url || '');
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [claimedAthlete, setClaimedAthlete] = useState<any>(null);

  const form = useForm<z.infer<typeof accountFormSchema>>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      display_name: currentUser?.display_name || '',
      email: currentUser?.email || '',
      bio: currentUser?.bio || '',
      location: currentUser?.location || '',
    },
  });

  // Fetch claimed athlete data for talent users
  useEffect(() => {
    const fetchClaimedAthlete = async () => {
      if (accountType !== 'talent' || !currentUser?.id) return;

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', currentUser.user_id)
          .maybeSingle();

        if (!profile) return;

        const { data: athlete } = await supabase
          .from('api_athletes')
          .select('*')
          .eq('claimed_by_profile_id', profile.id)
          .eq('claim_status', 'approved')
          .maybeSingle();

        if (athlete) {
          setClaimedAthlete(athlete);
          // Update avatar with athlete photo if available
          if (athlete.photo_url || athlete.espn_headshot_url) {
            setAvatarUrl(athlete.photo_url || athlete.espn_headshot_url || avatarUrl);
          }
          // Reset form with claimed athlete data
          const locationParts = [athlete.birthplace_city, athlete.birthplace_state].filter(Boolean);
          form.reset({
            display_name: athlete.name || currentUser?.display_name || '',
            email: currentUser?.email || '',
            bio: athlete.espn_bio || currentUser?.bio || '',
            location: locationParts.length > 0 ? locationParts.join(', ') : currentUser?.location || '',
          });
        }
      } catch (err) {
        console.error('Error fetching claimed athlete:', err);
      }
    };

    fetchClaimedAthlete();
  }, [accountType, currentUser?.id]);
  const onSubmit = async (values: z.infer<typeof accountFormSchema>) => {
    if (!currentUser) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: values.display_name,
          bio: values.bio || null,
          location: values.location || null,
          avatar_url: avatarUrl || null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', currentUser.user_id);

      if (error) throw error;

      // Update email in auth.users
      if (values.email !== currentUser.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: values.email
        });
        if (emailError) throw emailError;
      }

      await refreshUserData();
      toast({
        title: 'Success',
        description: 'Your account settings have been updated.',
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to update account settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset input up-front so the same file can be re-selected after a rejection.
    event.target.value = '';
    if (!file) return;

    // Only accept the four image MIME types we actually render. `accept="image/*"`
    // on the <input> is a UI hint that can be bypassed; this is the real gate.
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast({
        title: 'Unsupported file type',
        description: 'Please upload a JPEG, PNG, WebP, or GIF image.',
        variant: 'destructive',
      });
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast({
        title: 'Image too large',
        description: 'Please upload an image smaller than 5 MB.',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setIsCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async (blob: Blob) => {
    setIsCropOpen(false);
    setCropImageSrc(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const filePath = `${user.id}/${Date.now()}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, blob, { contentType: 'image/jpeg', upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Auto-save to profiles table
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      await refreshUserData();
      toast({ title: 'Avatar updated successfully' });
    } catch (error) {
      console.error('Avatar upload failed:', error);
      toast({ title: 'Upload failed', description: 'Could not upload avatar. Please try again.', variant: 'destructive' });
    }
  };

  const handleCropCancel = () => {
    setIsCropOpen(false);
    setCropImageSrc(null);
  };

  const handleLogout = async () => {
    // Mirror the logout flow used by SidebarNavigation / desktop top-nav:
    // clear UserContext, purge sb-* tokens from localStorage, sign out via
    // supabase, then hard-redirect to /auth.
    logout();
    Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    window.location.href = '/auth';
  };

  return (
    <div className="space-y-6">
      {cropImageSrc && (
        <AvatarCropDialog
          open={isCropOpen}
          imageSrc={cropImageSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>
            Update your personal information and how others see you on TheSlice.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-center space-x-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={avatarUrl} />
                  <AvatarFallback>
                    {currentUser?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Label htmlFor="avatar-upload" className="cursor-pointer">
                    <Button variant="outline" size="sm" asChild>
                      <span>
                        <Camera className="h-4 w-4 mr-2" />
                        Change Avatar
                      </span>
                    </Button>
                  </Label>
                  <Input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                  <p className="text-sm text-muted-foreground">
                    JPG, PNG or GIF. Max size 5MB.
                  </p>
                </div>
              </div>

              <FormField
                control={form.control}
                name="display_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your display name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bio</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell others about yourself..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your location" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sign Out</CardTitle>
          <CardDescription>
            Sign out of TheSlice on this device. You'll need to sign back in to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </CardContent>
      </Card>

      {/* Account deletion is handled manually via support during beta — once
          we have a self-service flow it can become a real button here. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">Delete Account</CardTitle>
          <CardDescription>
            Account deletion is handled by our support team. Email us and we'll
            permanently remove your account and all associated data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <a href={`mailto:support@theslice.ai?subject=${encodeURIComponent('Account deletion request')}&body=${encodeURIComponent(`I'd like to permanently delete my TheSlice account.\n\nAccount email: ${currentUser?.email ?? ''}\n`)}`}>
              <Trash2 className="h-4 w-4 mr-2" />
              Email support to delete
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};